package songwritersnotebook.app.components

import androidx.compose.animation.core.*
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.VolumeOff
import androidx.compose.material.icons.automirrored.filled.VolumeUp
import androidx.compose.material.icons.filled.Timer
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay

@Composable
fun MetronomeSettingItem(
    label: String,
    bpm: Int,
    onBpmChange: (Int) -> Unit,
    isPreviewActive: Boolean,
    onPreviewToggle: (Boolean) -> Unit,
    isMuted: Boolean,
    onMutedChange: (Boolean) -> Unit,
    currentBeat: Int,
    showDivider: Boolean = true,
    onBpmChangeFinished: () -> Unit = {}
) {
    Column(modifier = Modifier.padding(vertical = 8.dp)) {
        // Label and Value
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(label, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Bold)
            Text("$bpm BPM", style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.primary)
        }
        
        Spacer(Modifier.height(4.dp))
        
        // Slider
        var localBpm by remember { mutableFloatStateOf(bpm.toFloat()) }
        var isDragging by remember { mutableStateOf(false) }
        
        LaunchedEffect(bpm) {
            if (!isDragging) {
                localBpm = bpm.toFloat()
            }
        }
        
        Slider(
            value = localBpm,
            onValueChange = { 
                isDragging = true
                localBpm = it 
                onBpmChange(it.toInt()) 
            },
            onValueChangeFinished = {
                isDragging = false
                onBpmChangeFinished()
            },
            valueRange = 40f..250f,
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)
        )
        
        Spacer(Modifier.height(8.dp))
        
        // Controls Row
        Row(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth()
                .height(IntrinsicSize.Min)
                .border(
                    1.dp,
                    MaterialTheme.colorScheme.outline.copy(alpha = 0.5f),
                    MaterialTheme.shapes.medium
                ),
            verticalAlignment = Alignment.CenterVertically
        ) {
            TextButton(
                onClick = { onPreviewToggle(!isPreviewActive) },
                modifier = Modifier.weight(1f),
                colors = if (isPreviewActive) {
                    ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.primary)
                } else {
                    ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.onSurfaceVariant)
                }
            ) {
                Icon(
                    if (isPreviewActive) Icons.Default.Timer else Icons.Default.Timer, 
                    contentDescription = null, 
                    modifier = Modifier.size(20.dp)
                )
                Spacer(Modifier.width(8.dp))
                Text(if (isPreviewActive) "Stop Preview" else "Preview Tempo", fontSize = 14.sp)
            }

            VerticalDivider(
                modifier = Modifier
                    .fillMaxHeight()
                    .width(1.dp),
                color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
            )

            IconButton(
                onClick = { onMutedChange(!isMuted) },
                enabled = isPreviewActive
            ) {
                Icon(
                    if (isMuted) Icons.AutoMirrored.Filled.VolumeOff else Icons.AutoMirrored.Filled.VolumeUp,
                    contentDescription = "Mute",
                    modifier = Modifier.size(20.dp)
                )
            }

            VerticalDivider(
                modifier = Modifier
                    .fillMaxHeight()
                    .width(1.dp),
                color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
            )

            Box(modifier = Modifier.padding(horizontal = 16.dp), contentAlignment = Alignment.Center) {
                MetronomeVisualizer(beat = currentBeat)
            }
        }
        
        if (showDivider) {
            Spacer(Modifier.height(16.dp))
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
        }
    }
}

@Composable
fun MetronomeVisualizer(beat: Int, modifier: Modifier = Modifier) {
    var isLit by remember { mutableStateOf(false) }

    LaunchedEffect(beat) {
        if (beat != -1) {
            isLit = true
            delay(100L)
            isLit = false
        } else {
            isLit = false
        }
    }

    val color = if (isLit) MaterialTheme.colorScheme.primary else Color.Gray.copy(alpha = 0.5f)

    Box(
        modifier = modifier
            .size(20.dp)
            .background(color, CircleShape)
    )
}
